import { Injectable } from '@angular/core';

import {
  DlgDataAssetCar,
  DlgUserProfile,
  DlgDataQuote,
  DlgDataPolicyInfo,
  DlgDataError,
  DlgDataRenewalPreference,
  DlgDataAction,
} from 'dlg-dolphin/dist/lib/interfaces/dlg-data';

import { MapperFactory as MapperStp } from '@app/core/services/analytics/mappers/analytical-mappers';
import { MapperFactory as MapperRen } from '@ren/core/services/analytics/analytics-mappers';
import { Utility } from '@global/infrastructure/helpers/utility';
import { KeyValueDict } from '@global/infrastructure/interfaces/key-value-dict';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private quote: DlgDataQuote;
  private policy: DlgDataPolicyInfo;

  private static mapFeature(featureName: string, formData: any) {
    if (Utility.isMta()) {
      return MapperStp.create(featureName).mapFeature(formData);
    } else {
      return MapperRen.create(featureName).mapFeature(formData);
    }
  }

  static trackApi({
    name,
    error,
    call_type,
    response_value,
  }: {
    name?: string;
    error?: string;
    response_value?: boolean;
    call_type?: 'request' | 'response' | 'failed';
  }) {
    if (window?.dlg_dolphin?.api) {
      window.dlg_dolphin.api.trackApi({
        ...(name && { name }),
        ...(error && { error }),
        ...(call_type && { call_type }),
        ...(call_type === 'response' && { response_value }),
      });
    }
  }

  trackPage(): void {
    window.dlg_dolphin.trackPage();
    if (window?.dlg_data?.page) {
      if (/\s/.test(window.dlg_data.page.name)) {
        const pageName = window.dlg_data.page.name.split('-');
        if (pageName.length) {
          const pageNameLast = pageName[pageName.length - 1].replace(/\s/g, '');
          if (
            /^[a-zA-Z\d]{8,}$/.test(pageNameLast) &&
            /\d/.test(pageNameLast)
          ) {
            pageName.splice(-1);
            window.dlg_data.page.name = pageName.join('-').trim();
          }
        }
      } else if (/\//.test(window.dlg_data.page.name)) {
        window.dlg_data.page.name =
          window.dlg_data.page.name.split('?token=')[0];
        window.dlg_data.page.name = window.dlg_data.page.name
          .split('/')
          .filter((urlFragment) => (urlFragment.match(/-/g) || []).length <= 1)
          .join('/');
      }
    }
  }

  trackSource(site?: string): void {
    window.dlg_dolphin.trafficSource.trackTrafficSource(site);
  }

  trackError(data?: DlgDataError) {
    window.dlg_dolphin.error.trackError(data);
  }

  trackAction(data): void {
    const action: DlgDataAction = {
      event_name: data.event_name,
      event_time: data.event_time,
      attributes: {
        component_name: data.component_name,
        link_name: data.link_name,
      },
    };
    if (
      window?.dlg_data?.action[window.dlg_data.action.length - 1]
        ?.event_time !== data.event_time
    ) {
      window.dlg_data.action.push(action);
    }
  }

  trackFeature(featureName: string, formData: any): void {
    const data = AnalyticsService.mapFeature(featureName, formData);
    const policyInfo = window?.dlg_data?.policy?.info;

    switch (featureName) {
      case 'yourCarPerm':
        window.dlg_dolphin.asset.trackAssetCar(data as DlgDataAssetCar);
        break;
      case 'yourDetails':
        if (Utility.isMta()) {
          window.dlg_dolphin.user.trackUser(data as DlgUserProfile);
        } else if (data?.length > 0) {
          window.dlg_data.user = [];
          data.forEach((user) => {
            window.dlg_dolphin.user.trackUser(user as DlgUserProfile);
          });
        }
        break;
      case 'policy':
        this.policy = {
          ...this.policy,
          ...(policyInfo?.length > 0 && policyInfo[policyInfo.length - 1]),
          ...data,
        };
        window.dlg_dolphin.policy.clearPolicyInfo();
        window.dlg_dolphin.policy.trackPolicyOrQuote(this.policy);
        break;
      case 'quote':
        this.quote = {
          ...window?.dlg_data?.quote,
          ...this.quote,
          ...data,
        };
        window.dlg_dolphin.quote.trackMotorQb(this.quote);
        break;
      default:
        break;
    }
  }

  trackRenewalPreference(preference, autoRenewalValue) {
    const mappedPreference: DlgDataRenewalPreference =
      preference === autoRenewalValue ? 'Auto' : 'Manual';
    window.dlg_dolphin.policy.trackRenewalPreference(mappedPreference);
  }

  trackPaymentResponse(status: string, orderKey: string, errorRef: string) {
    window.dlg_dolphin.payment.trackPaymentResponse({
      status,
      orderKey,
      errorRef,
    });
  }

  removedTrackedFeature(featureName: string, index) {
    switch (featureName) {
      case 'yourDetails':
        window.dlg_dolphin.user.removeTrackedUser(index);
        break;
      case 'yourCarPerm':
        window.dlg_dolphin.asset.removeTrackedAssetCar(index);
        break;
      default:
        break;
    }
  }

  driverAdded() {
    const added: number =
      (window.dlg_data.quote.drivers_added
        ? window.dlg_data.quote.drivers_added
        : 0) + 1;
    this.trackFeature('quote', { drivers_added: added });
  }

  driverRemoved() {
    const removed: number =
      (window.dlg_data.quote.drivers_removed
        ? window.dlg_data.quote.drivers_removed
        : 0) + 1;
    this.trackFeature('quote', { drivers_removed: removed });
  }

  trackAutoRenewalOptInOut(autoRenewal: string) {
    if (window.dlg_data) {
      window.dlg_data.policy.auto_renewal = autoRenewal;
    }
  }

  trackAutoRenewalAction(isAutoRenewal: boolean) {
    if (isAutoRenewal === true) {
      const eventTime = new Date();
      eventTime.setSeconds(eventTime.getSeconds() + 1);
      this.trackAction({
        event_name:
          'Link:Automatically renew this policy using this payment method',
        event_time: eventTime.toLocaleString('en-GB').replace(',', ''),
        component_name: 'confirmation_autoRenewal',
        link_name: 'Automatically renew this policy using this payment method',
      });
    } else {
      window.dlg_data.action = window.dlg_data.action.filter(
        (action) =>
          action?.attributes?.component_name !== 'confirmation_autoRenewal'
      );
    }
  }

  trackPolicyCancelReason(reason: string) {
    if (window.dlg_data) {
      window.dlg_data.policy.cancellation = { reason_for_enquiry: '' };
      window.dlg_data.policy.cancellation.reason_for_enquiry = reason;
    }
  }

  trackLicenceChanges(
    initialLicences: { id: string; licenceType: string }[],
    updatedLicences: { id: string; licenceType: string }[]
  ) {
    window.dlg_dolphin.policy.trackLicenceChanges(
      initialLicences,
      updatedLicences
    );
  }

  formatAddons(offerings) {
    let addOns = {};
    const addOnsArray = [];
    offerings.forEach((offering) => {
      offering?.coverages?.vehicleCoverages?.forEach((coverage) => {
        coverage?.rescueCoverages_dlg
          ?.filter((c) => c.selected)
          .forEach((rescueCoverage) => {
            addOnsArray.push({
              name: rescueCoverage.covName,
              price: rescueCoverage.amount.amount,
            });
          });
        coverage?.coverages
          ?.filter((c) => c.selected)
          .forEach((extra) => {
            if (extra?.amount?.amount) {
              addOnsArray.push({
                name: extra.codeIdentifier_dlg,
                price: extra.amount.amount,
              });
            }
          });
      });
    });
    if (addOnsArray.length) {
      addOns = { add_ons: addOnsArray };
    }
    return addOns;
  }

  trackPolicy(data) {
    const coverPolicies =
      window.dlg_data.policy.info.filter(
        (policy) => policy.cover_level === data.cover_level
      ) || [];

    if (coverPolicies.length === 0) {
      window.dlg_dolphin.policy.trackPolicyOrQuote(data);
    } else {
      const policyInfo = coverPolicies[coverPolicies.length - 1];

      if (
        policyInfo.price !== data.price ||
        JSON.stringify(policyInfo?.add_ons) !== JSON.stringify(data?.add_ons)
      ) {
        window.dlg_dolphin.policy.trackPolicyOrQuote(data);
      }
    }
  }

  trackQuoteChanges(alterOptions: KeyValueDict<boolean>) {
    const changes = Object.keys(alterOptions)
      .map((key) => {
        return alterOptions[key] ? key : undefined;
      })
      .filter((item) => item !== undefined);

    const renameChanges = changes.map((item: string) => {
      return item.toLowerCase() === 'yourcar'
        ? 'Vehicle'
        : item.toLowerCase() === 'yourdrivers'
        ? 'Driver'
        : item;
    });
    this.trackFeature('quote', {
      changes: [{ name: renameChanges.join(', ') }],
    });
  }
}
